import React, { useEffect, useState } from 'react';
import {
  Skeleton,
  Paper,
  Typography,
  Box,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import DateRangeFilter from '../common/DateRangeFilter';
import { Report } from '../../models/report';
import { DIMENSION_LABELS as ALL_DIMENSION_LABELS, formatPeriodLabel } from '../../utils/periodLabels';
import { daysAgoVN, todayVN } from '../../utils/vnDate';

const DIMENSION_LABELS = {
  day: ALL_DIMENSION_LABELS.day,
  week: ALL_DIMENSION_LABELS.week,
  month: ALL_DIMENSION_LABELS.month,
};

// Defaults are Vietnam calendar dates (last 30 days), not the UTC date.
const getDefaultStartDate = () => daysAgoVN(30);
const getDefaultEndDate = () => todayVN();

const OrderCountReport = (props) => {
  const { setPageTitle } = props;
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [startDate, setStartDate] = useState(getDefaultStartDate());
  const [endDate, setEndDate] = useState(getDefaultEndDate());
  const [dimension, setDimension] = useState('day');

  useEffect(() => {
    const fetchReport = async () => {
      if (!startDate || !endDate) {
        setData([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        setPageTitle('Thống kê số đơn');

        const startIso = `${startDate}T00:00:00+07:00`;
        const endIso = `${endDate}T23:59:59+07:00`;

        const response = await Report.fetch('order_count', {
          start_date: startIso,
          end_date: endIso,
          dimension,
        });

        const chartData = response.map((item) => ({
          ...item,
          label: formatPeriodLabel(item.period, dimension),
        }));

        setData(chartData);
      } catch (error) {
        console.error('Error fetching order count report:', error);
        setData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, [startDate, endDate, dimension, setPageTitle]);

  const handleDimensionChange = (event, newDimension) => {
    if (newDimension !== null) {
      setDimension(newDimension);
    }
  };

  return (
    <Box sx={{ maxWidth: 1200, margin: '24px auto', pl: 3, pr: 3 }}>
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
        <DateRangeFilter
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
        />
        <ToggleButtonGroup
          value={dimension}
          exclusive
          onChange={handleDimensionChange}
          size="small"
        >
          {Object.entries(DIMENSION_LABELS).map(([key, label]) => (
            <ToggleButton key={key} value={key}>
              {label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      {loading ? (
        <Skeleton variant="rectangular" width="100%" height={400} />
      ) : data.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            {!startDate || !endDate
              ? 'Vui lòng chọn khoảng thời gian để xem báo cáo'
              : 'Không tìm thấy dữ liệu báo cáo cho khoảng thời gian đã chọn.'}
          </Typography>
        </Paper>
      ) : (
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Số đơn theo {DIMENSION_LABELS[dimension].toLowerCase()}
          </Typography>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="label"
                angle={dimension === 'day' ? -45 : 0}
                textAnchor={dimension === 'day' ? 'end' : 'middle'}
                height={dimension === 'day' ? 80 : 40}
                fontSize={12}
              />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Legend />
              <Bar
                dataKey="instant_order"
                name="Đơn lẻ"
                stackId="a"
                fill="#5B8DB8"
              />
              <Bar
                dataKey="monthly_package_order"
                name="Gói tháng"
                stackId="a"
                fill="#F0A050"
              />
            </BarChart>
          </ResponsiveContainer>
        </Paper>
      )}
    </Box>
  );
};

export default OrderCountReport;
